import {
    Button,
    Checkbox,
    CheckboxGroup,
    DatePicker,
    RadiobuttonGroup, Select, UploadArea
} from '@usitsdasdesign/dds-react';
import Input from 'atoms/Input';
import TextArea from 'atoms/TextArea';
import RichTextEditor from 'components/RichTextEditor';
import SearchableSelect from 'molecules/SearchableSelect';
import moment from 'moment';
import AddUsers from 'organisms/AddUsers';
import AddUsersRA from 'organisms/AddUsersRA';
import PropTypes from 'prop-types';
import AsyncReactSelectPaginate from './AsyncReactSelectPaginate';
import AsyncReactSelectPaginateWithSelectAll from './AsyncReactSelectPaginateWithSelectAll';
import AsyncSelectPaginateV2 from './AsyncSelectPaginateV2';
import DatePickerV2 from './DatePicker';
import DynamicFields from './DynamicFields';
import InputToggle from './InputToggle';
import InputWithTags from './InputWithTags';

function getErrorsFromArray(name, errors) {
    if (errors[name]?.message) {
        return errors[name].message;
    }
    const arrErrors = Object.keys(errors).filter((errKey) => errKey.includes(name));
    if (arrErrors?.length) {
        const msg = arrErrors.map((err) => `${err.slice(name.length)}: ${errors[err].message}`).join(', ');
        return msg
    }
    return '';
}

function RenderFields({
    fields, grid, gap = 'gap-5', className, control, register, errors, setValue, watch, fieldSize, useMinDate = true, partnerSearchText, setPartnerSearchText
}) {    
    const handleOptionChange = (selected, options, name) => {
        const isSelectAll = selected.filter(option => option.value !== "*")
        if (selected && selected.some(option => option.value === "*")) {
            if (isSelectAll.length === options.length) {
                setValue(name, null);
                setValue('isAllSelectedOptionActive', false);
            }
            else {
                setValue(name, options);
                setValue('isAllSelectedOptionActive', true);
            }
        }
        else {
            setValue(name, selected);
            setValue('isAllSelectedOptionActive', isSelectAll.length === options.length);
        }
    };
    const getNestedValue = (obj, path) => {
        if (path in obj) return obj[path];
        const normalizedPath = path.replace(/\[(\d+)\]/g, '.$1');
        return normalizedPath.split('.').reduce((o, key) => (o ? o[key] : undefined), obj);
    };
    return (
        <div className={`grid ${gap} ${className} ${grid}`}>
            {
                fields.map((field) => {
                    const {
                        type, name, id, hidden = false, ...rest
                    } = field;
                    if (hidden) return null;
                    switch (type) {
                        case 'text-input':
                            return (
                                <Input
                                    key={id}
                                    value={watch(name)}
                                    valueChanged={(val) => setValue(name, val, { shouldDirty: true, shouldValidate: true })}
                                    isError={name in errors}
                                    errorMessage={errors[name]?.message}
                                    size={fieldSize}
                                    {...rest}
                                />
                            );
                        case 'calendor-old':
                            return (
                                <DatePicker
                                    key={id}
                                    format={'MM/DD/YYYY'}
                                    value={watch(name)}
                                    dateSelected={(data) => {
                                        if (rest.canChangeDate) {
                                            const { allowed, prevVal } = rest.canChangeDate(data, name);
                                            if (!allowed) {
                                                setValue(name, new Date(prevVal), { shouldDirty: true, shouldValidate: true });

                                                if (rest.showError) {
                                                    rest.showError(data, name);
                                                }
                                                return;
                                            }
                                        }
                                        setValue(name, data, { shouldDirty: true, shouldValidate: true });
                                        if (rest.onDateChange) {
                                            rest.onDateChange(data);
                                        }
                                    }}
                                    isError={name in errors}
                                    errorMessage={errors[name]?.message}
                                    stickerOptions={{
                                        position: 'bottom',
                                        isDynamic: false,
                                    }}
                                    icon="calendar__s__stroke"
                                    isManualInput
                                    inputByFormatMask
                                    {...rest}
                                />
                            );
                        case 'calendor':
                            return (
                                <DatePickerV2
                                    key={id}
                                    format={'MM/DD/YYYY'}
                                    value={watch(name)}
                                    dateSelected={(data) => {
                                        if (rest.canChangeDate) {
                                            const { allowed, prevVal } = rest.canChangeDate(data, name);
                                            if (!allowed) {
                                                setValue(name, new Date(prevVal), { shouldDirty: true, shouldValidate: true });

                                                if (rest.showError) {
                                                    rest.showError(data, name);
                                                }
                                                return;
                                            }
                                        }
                                        setValue(name, rest.returnFormattedDate ? moment(data, 'MM/DD/YYYY').format('YYYY-MM-DD') : data, { shouldDirty: true, shouldValidate: true });
                                        if (rest.trigger) {
                                            if (name.endsWith('startDate')) {
                                                rest.trigger(name.replace('startDate', 'endDate'));
                                            } else if (name.endsWith('endDate')) {
                                                rest.trigger(name.replace('endDate', 'startDate'));
                                            }
                                        }
                                        if (rest.onChange) {
                                            rest.onChange(data);
                                        }

                                    }}
                                    isError={!!getNestedValue(errors, name)}
                                    errorMessage={getNestedValue(errors, name)?.message}
                                    stickerOptions={{
                                        position: 'bottom',
                                    }}
                                    icon="calendar__s__stroke"
                                    isManualInput
                                    inputByFormatMask
                                    {...rest}
                                    {...((useMinDate && !rest?.minDate && name === 'endDate') ? { minDate: moment(watch('startDate')).toDate() } : {})}
                                />
                            );
                        case 'select':
                            return (
                                <Select
                                    value={watch(name)}
                                    key={id}
                                    {...rest}
                                />
                            );
                        case 'select-searchable':
                            return (
                                <SearchableSelect
                                    key={id}
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    name={name}
                                    isError={!!getNestedValue(errors, name)}
                                    defaultValue={watch(name)}
                                    errorMessage={getNestedValue(errors, name)?.message}
                                    {...rest}
                                    onChange={(e) => { 
                                        if(rest?.changeCallback){
                                            if(rest?.options?.length === e?.length && rest?.isSelectAll){
                                                rest?.changeCallback([...e, {label: 'Select all', value: '*'}])
                                           } else {
                                                rest?.changeCallback(e)
                                           }
                                        }                                      
                                        if (rest?.isSelectAll) {
                                            handleOptionChange(e, rest?.options, name);
                                            if (rest.onChange) {
                                                rest.onChange(e);
                                            }
                                        }
                                        else {
                                            setValue(name, e, { shouldDirty: true, shouldValidate: true });
                                            if (rest.onChange) {
                                                rest.onChange(e);
                                            }
                                        }

                                    }}
                                />
                            );
                        case 'async-select-paginate':
                            return (
                                <AsyncReactSelectPaginate
                                    key={[id, errors]}
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    name={name}
                                    isError={Boolean(getErrorsFromArray(name, errors))}
                                    defaultValue={watch(name)}
                                    errorMessage={getErrorsFromArray(name, errors)}
                                    {...(!rest?.key ? { key: id } : {})}
                                    {...rest}
                                    {...(
                                        ['engagementPartner', 'reportingPartnerName', 'partner'].includes(name) || true
                                            ? {
                                                searchTextxx: partnerSearchText,
                                                onInputChange: setPartnerSearchText
                                            }
                                            : {}
                                    )}
                                />
                            );

                        case 'async-select-paginate-selectAll':

                            return (
                                <AsyncReactSelectPaginateWithSelectAll
                                    key={id}
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    name={name}
                                    isError={name in errors}
                                    defaultValue={watch(name)}
                                    errorMessage={errors[name]?.message}
                                    {...(!rest?.key ? { key: id } : {})}
                                    {...rest}
                                />
                            );
                        case 'async-select-paginate-v2':
                            return (
                                <AsyncSelectPaginateV2
                                    key={id}
                                    id={id}
                                    control={control}
                                    register={register}
                                    name={name}
                                    watch={watch}
                                    isMulti={true}
                                    setValue={setValue}
                                    isError={name in errors}
                                    errorMessage={errors[name]?.message}
                                    isPaginate={true}
                                    {...rest}
                                />
                            );
                        case 'number-input':
                            return (
                                <Input
                                    type='number'
                                    key={id}
                                    className='remove_ChangeValueButton'
                                    value={watch(name)}
                                    valueChanged={(val) => setValue(name, val, { shouldDirty: true, shouldValidate: true })}
                                    isError={!!getNestedValue(errors, name)}
                                    errorMessage={getNestedValue(errors, name)?.message}
                                    {...rest}
                                />
                            );
                        case 'radio-group':
                            return (
                                <div className='radio-group-container'>
                                    <RadiobuttonGroup
                                        key={id}
                                        onChangeHandler={(val) => {
                                            setValue(name, val, { shouldDirty: true, shouldValidate: true });
                                            if (rest?.fromRequisition) {
                                                rest?.onChange && rest?.onChange(val);
                                            }
                                        }}
                                        isError={name in errors}
                                        {...rest}
                                    />
                                    {
                                        errors[name]?.message
                                        && <span className="text-red-500 text-xs mt-2 inline-block">
                                            {errors[name]?.message}
                                        </span>
                                    }
                                </div>
                            );
                        case 'button':
                            return (
                                <Button
                                    {...rest}
                                />
                            );
                        case 'upload':
                            return (
                                <UploadArea
                                    key={id}
                                    errorMessage={errors[name]?.message}
                                    isError={name in errors}
                                    dragAndDropText='Drag and Drop here or Browse'
                                    customClass={`drag-and-drop-upload ${rest?.className} ${name in errors ? 'error-in-field' : ''}`}
                                    {...rest}
                                />
                            );
                        case 'text-area':
                            return (
                                <TextArea
                                    value={watch(name)}
                                    onValueChange={(val) => setValue(name, val, { shouldDirty: true, shouldValidate: true })}
                                    errorMessage={errors[name]?.message}
                                    isError={name in errors}
                                    {...rest}
                                />
                            );
                        case 'field-group':
                            return (
                                <DynamicFields
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    setValue={setValue}
                                    watch={watch}
                                    name={name}
                                    {...rest}
                                    onDeleteRow={rest?.onDeleteRow}
                                />
                            );
                        case 'rich-text-editor':
                            return (
                                <RichTextEditor
                                    label='Job Description'
                                    control={control}
                                    errors={errors}
                                    name={name}
                                    {...rest}
                                />
                            );
                        case 'check-box':
                            return (
                                <Checkbox
                                    register={register}
                                    name={name}
                                    onChangeHandler={(data) => {
                                        setValue(name, data.value, { shouldValidate: true })
                                    }}
                                    {...rest}
                                />
                            );
                        case 'checkbox-group':
                            return (
                                <CheckboxGroup
                                    onChangeHandler={(val) => {
                                        setValue(name, val, { shouldValidate: true })
                                    }}
                                    isError={name in errors}
                                    {...rest}
                                />

                            );
                        case 'add-users':
                            return (
                                <AddUsers
                                    control={control}
                                    register={register}
                                    name={name}
                                    isError={name in errors}
                                    errorMessage={errors[name]?.message}
                                    values={watch(name)}
                                    setValue={setValue}
                                    {...rest}
                                />

                            );
                        case 'add-users-ra':
                            return (
                                <AddUsersRA
                                    control={control}
                                    register={register}
                                    name={name}
                                    isError={name in errors}
                                    errorMessage={errors[name]?.message}
                                    values={watch(name)}
                                    setValue={setValue}
                                    {...rest}
                                />

                            );
                        case 'input-with-tags':
                            return (
                                <InputWithTags
                                    key={id}
                                    initialTags={['sf', 'dd']}
                                    value={watch(name)}
                                    valueChanged={(val) => setValue(name, val, { shouldDirty: true, shouldValidate: true })}
                                    isError={name in errors}
                                    errorMessage={errors[name]?.message}
                                    {...rest}
                                />
                            );
                        case 'input-toggle':
                            return (
                                <InputToggle
                                    key={id}
                                    onChangeHandler={(val) => {
                                        setValue(name, val, { shouldValidate: true })
                                    }}
                                    {...rest}
                                />
                            );
                        case 'key-value':
                            return (
                                <div key={id} className={`flex flex-col ${rest.className || ''}`}>
                                    {rest.label && (
                                        <label className="text-gray-1000 text-xs mb-1 font-normal leading-4">
                                            {rest.label}
                                        </label>
                                    )}
                                    <div className="text-sm font-bold text-left text-black leading-5">
                                        {rest.value || '-'}
                                    </div>
                                </div>
                            );
                        case 'custom-display':
                            return (
                                <div key={id} className={`flex flex-col ${rest.className || ''}`}>
                                    {rest.label && (
                                        <label className="text-gray-1000 text-xs mb-1 font-normal leading-4">
                                            {rest.label}
                                        </label>
                                    )}
                                    <div className="text-sm font-bold text-left text-black leading-5">
                                        {typeof rest.value === 'function' ? rest.value() : rest.value}
                                    </div>
                                </div>
                            );
                        default:
                            return null;
                    }
                })
            }
        </div>
    );
}

RenderFields.defaultProps = {
    fields: [],
    grid: 'grid-cols-1 md:grid-cols-2',
    className: '',
    control: {},
    register: () => false,
    errors: {},
    setValue: () => false,
    watch: () => false,
    fieldSize: 'l'
};

RenderFields.propTypes = {
    fields: PropTypes.arrayOf(PropTypes.shape({})),
    grid: PropTypes.string,
    className: PropTypes.string,
    control: PropTypes.object,
    register: PropTypes.func,
    errors: PropTypes.object,
    setValue: PropTypes.func,
    watch: PropTypes.func,
    fieldSize: PropTypes.oneOf(['l', 'm', 's'])
};

export default RenderFields;
